import React, { useState, useEffect, useContext } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  Alert,
  ButtonGroup,
  Spinner,
  InputGroup,
  Badge,
  Tabs,
  Tab
} from 'react-bootstrap';
import {
  FaPlus,
  FaSync,
  FaTasks,
  FaList,
  FaTh,
  FaSearch,
  FaUser
} from 'react-icons/fa';
import TaskCard from '../components/TaskCard';
import CreateTaskModal from '../components/CreateTaskModal';
import TaskListTable from '../components/TaskListTable';
import TaskStatistics from '../components/TaskStatistics';
import AttentionTab from '../components/tasks/AttentionTab';
import PerformanceTab from '../components/tasks/PerformanceTab';
import GuestAssessmentModal from '../components/GuestAssessmentModal';
import taskApi from '../services/taskApi';
import secureApiService from '../services/secureApi';
import { formatDate } from '../utils/dateFormatter';
import { formatCount } from '../utils/numberFormatter';
import { taskRefusalKey } from '../utils/taskErrorCodes';
import { TASK_TYPE_FILTERS } from '../config/taskTypes';
import DesktopLayout from '../layouts/DesktopLayout';
import useLanguage from '../hooks/useLanguage';
import { AuthContext } from '../context/AuthContext';
import { TASK_MANAGER_ROLES } from '../config/roleConfig';
import { useSearchParams } from 'react-router-dom';
import '../styles/TasksPage.css';

const Tasks = () => {
  const { t, isArabic, language } = useLanguage();
  const { user } = useContext(AuthContext);

  /**
   * «المتابعة» sits beside the working list for whoever supervises the work — admin, ops manager,
   * maintenance supervisor (tasks/supervisor-view.md D1/D2). Plain `ops` never sees the tab bar:
   * it is a deciding view, not a doing view, and their page is unchanged.
   *
   * Which tab is open lives in the URL, not in state — so `/tasks?view=attention` can be linked to,
   * survives a refresh, and is where a supervisor's login lands.
   */
  const canSupervise = TASK_MANAGER_ROLES.includes(user?.role);
  // Bumped by the page's refresh button so it reloads whichever tab is actually on screen.
  const [refreshSignal, setRefreshSignal] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  // «تقارير الأداء» (tasks/cleaning-performance-report.md step 5) is the third tab, same audience.
  const SUPERVISOR_VIEWS = ['attention', 'performance'];
  const requestedView = searchParams.get('view');
  const activeTab = canSupervise && SUPERVISOR_VIEWS.includes(requestedView) ? requestedView : 'worklist';
  const setActiveTab = (key) => {
    const next = new URLSearchParams(searchParams);
    if (SUPERVISOR_VIEWS.includes(key)) next.set('view', key);
    else next.delete('view');
    setSearchParams(next, { replace: true });
  };

  // `?task=<id>` opens that task's dialog over «المتابعة» — the cleaning arrival notification lands
  // here (tasks/cleaning-manager-arrival-alert.md). Cleared once handled, so a refresh or a closed
  // dialog does not reopen it.
  const deepLinkTaskId = activeTab === 'attention' ? searchParams.get('task') : null;
  const clearDeepLinkTask = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('task');
    setSearchParams(next, { replace: true });
  };

  const [tasks, setTasks] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [activeNavItem, setActiveNavItem] = useState('tasks');

  // View modes
  const [viewMode, setViewMode] = useState(() => window.innerWidth <= 768 ? 'cards' : 'list'); // 'cards' | 'list' | 'calendar'
  const [groupBy, setGroupBy] = useState('priority'); // 'priority' | 'property' | 'status' | 'date'

  // Filters (server-affecting — triggers API re-fetch)
  const [filters, setFilters] = useState({
    dateFilter: 'all', // 'today' | 'week' | 'month' | 'all' - Changed default to 'all'
    statusFilter: 'active', // 'all' | 'active' | 'pending' | 'assigned' | 'in-progress' | 'completed'
    priorityFilter: 'all', // 'all' | 'critical' | 'urgent' | 'high' | 'medium' | 'low'
    propertyFilter: 'all', // 'all' | propertyId
    assigneeFilter: 'all', // 'all' | 'unassigned'
    bookingFilter: 'all', // 'all' | 'with-booking' | 'without-booking'
    typeFilter: 'all', // 'all' | 'cleaning' | 'maintenance' | 'restock'
  });

  // Search (client-side only — does NOT trigger API re-fetch)
  const [searchQuery, setSearchQuery] = useState('');

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [assessingTask, setAssessingTask] = useState(null);
  const [bulkSkipped, setBulkSkipped] = useState(0);

  // Statistics
  const [statistics, setStatistics] = useState({
    total: 0,
    critical: 0,
    urgent: 0,
    pending: 0,
    inProgress: 0,
    completed: 0,
    overdue: 0
  });

  // Load properties for filter dropdown
  const loadProperties = async () => {
    try {
      const data = await secureApiService.getProperties();
      setProperties(data || []);
    } catch (err) {
      console.error('Failed to load properties:', err);
    }
  };

  // Load tasks
  const loadTasks = async (showLoadingSpinner = true) => {
    if (showLoadingSpinner) setLoading(true);
    setError(null);

    try {
      // Build API filters
      const apiFilters = {};

      // Date filter — the SERVER resolves the window in the business timezone.
      // This used to compute the dates here via toISOString(), which returns the
      // UTC day and so asked for yesterday's window between 00:00 and 03:00 Riyadh.
      if (['today', 'week', 'month'].includes(filters.dateFilter)) {
        apiFilters.businessRange = filters.dateFilter;
      }

      // Status filter
      if (filters.statusFilter === 'active') {
        // Active means not completed or cancelled
      } else if (filters.statusFilter !== 'all') {
        apiFilters.status = filters.statusFilter;
      }

      // Priority filter
      if (filters.priorityFilter !== 'all') {
        apiFilters.priority = filters.priorityFilter;
      }

      // Property filter
      if (filters.propertyFilter !== 'all') {
        apiFilters.propertyId = filters.propertyFilter;
      }

      // Assignee filter
      if (filters.assigneeFilter === 'unassigned') {
        apiFilters.unassigned = true;
      } else if (filters.assigneeFilter === 'mine' && user?.userId) {
        apiFilters.assignedTo = user.userId;
      } else if (filters.assigneeFilter !== 'all') {
        apiFilters.assignedTo = filters.assigneeFilter;
      }

      // Task source filter
      if (filters.typeFilter !== 'all') {
        apiFilters.type = filters.typeFilter;
      }

      const response = await taskApi.getTasks(apiFilters);
      let fetchedTasks = response.data.tasks || [];

      // Apply client-side filters
      if (filters.statusFilter === 'active') {
        fetchedTasks = fetchedTasks.filter(t =>
          t.status !== 'completed' && t.status !== 'cancelled'
        );
      }

      // Booking filter (client-side)
      if (filters.bookingFilter === 'with-booking') {
        fetchedTasks = fetchedTasks.filter(t => t.booking?.confirmationCode);
      } else if (filters.bookingFilter === 'without-booking') {
        fetchedTasks = fetchedTasks.filter(t => !t.booking?.confirmationCode);
      }

      // Sort by earliest scheduled date (ascending) - so ops see today and next few days first
      fetchedTasks.sort((a, b) => {
        const dateA = new Date(a.scheduledDate);
        const dateB = new Date(b.scheduledDate);
        return dateA - dateB; // Ascending order (earliest first)
      });

      setTasks(fetchedTasks);

      // Calculate statistics
      const stats = {
        total: fetchedTasks.length,
        critical: fetchedTasks.filter(t => t.priority === 'critical').length,
        urgent: fetchedTasks.filter(t => t.priority === 'urgent').length,
        pending: fetchedTasks.filter(t => t.status === 'pending').length,
        inProgress: fetchedTasks.filter(t => t.status === 'in-progress').length,
        completed: fetchedTasks.filter(t => t.status === 'completed').length,
        overdue: fetchedTasks.filter(t => t.isOverdue).length
      };
      setStatistics(stats);

    } catch (err) {
      console.error('Load tasks error:', err);
      setError(t('tasks.page.loadError'));
    }

    if (showLoadingSpinner) setLoading(false);
  };

  // Load data on mount
  useEffect(() => {
    loadProperties();
    loadTasks();
  }, []); // eslint-disable-line

  // Reload when filters change
  useEffect(() => {
    loadTasks();
  }, [filters]); // eslint-disable-line

  // Refresh tasks
  const handleRefresh = async () => {
    setRefreshing(true);
    setRefreshSignal(n => n + 1);   // the attention tab reloads on this
    await loadTasks(false);
    setRefreshing(false);
  };

  // Task actions
  // The table's status dropdown calls these directly (no card-level catch), so a
  // refusal — e.g. an unfinished cleaning checklist — must surface here or it
  // looks like nothing happened.
  // A turnover clean that asks the guest question closes through the dialog, whichever control was
  // used — card button, card status, table row, table dropdown (L7). Returns true when it did.
  const openGuestQuestion = (taskId) => {
    const task = tasks.find(item => item._id === taskId);
    if (!task?.guestAssessmentPrompt) return false;
    setAssessingTask(task);
    return true;
  };

  // A refusal in the reader's language — from the server's code when it sends a known one.
  const refusalMessage = (err) => {
    const key = taskRefusalKey(err?.response?.data);
    return key ? t(key) : (err?.response?.data?.error || t('tasks.card.errorStatus'));
  };

  // Resolves to false when the status did NOT change (the dialog opened, or the server refused and
  // the banner says why) — TaskCard then keeps its select where it was.
  const handleStatusUpdate = async (taskId, newStatus) => {
    if (newStatus === 'completed' && openGuestQuestion(taskId)) return false;
    try {
      setError(null);
      await taskApi.updateTaskStatus(taskId, newStatus);
      await loadTasks(false);
      return true;
    } catch (err) {
      setError(refusalMessage(err));
      return false;
    }
  };

  const handleAssign = async (taskId, assignedTo) => {
    await taskApi.assignTask(taskId, assignedTo);
    await loadTasks(false);
  };

  // The table row's complete button. No rethrow: TaskCard no longer calls this, so the page banner is
  // the one place a refusal shows (a rethrow here was an unhandled rejection).
  const handleComplete = async (taskId) => {
    if (openGuestQuestion(taskId)) return false;
    try {
      setError(null);
      await taskApi.updateTaskStatus(taskId, 'completed');
      await loadTasks(false);
      return true;
    } catch (err) {
      setError(refusalMessage(err));
      return false;
    }
  };

  // Bulk close can't answer the guest question: a clean where it is required stays open and is
  // counted, never silently dropped. Checked against the FULL list — a selected row the search box
  // has hidden still counts.
  const handleBulkComplete = async (taskIds) => {
    const needsAnswer = taskIds.filter(id =>
      tasks.find(item => item._id === id)?.guestAssessmentPrompt?.required);
    setError(null);
    setBulkSkipped(needsAnswer.length);
    const results = await Promise.allSettled(
      taskIds
        .filter(id => !needsAnswer.includes(id))
        .map(id => taskApi.updateTaskStatus(id, 'completed'))
    );
    const refused = results.find(result => result.status === 'rejected');
    if (refused) setError(refusalMessage(refused.reason));
    await loadTasks(false);
  };

  const handleEdit = async () => {
    // Reload tasks after edit
    await loadTasks(false);
  };

  // Errors are left to the dialog, which shows why the close was refused.
  const handleGuestQuestionConfirm = async (guestAssessment) => {
    await taskApi.updateTaskStatus(assessingTask._id, 'completed', { guestAssessment });
    setAssessingTask(null);
    await loadTasks(false);
  };

  // Export to CSV (UI button planned for Phase 2)
  // eslint-disable-next-line no-unused-vars
  const handleExport = () => {
    const csvContent = [
      [t('tasks.page.csvTask'), t('tasks.page.csvProperty'), t('tasks.page.csvPriority'), t('tasks.page.csvStatus'), t('tasks.page.csvDate'), t('tasks.page.csvAssignee')],
      ...tasks.map(task => [
        task.title,
        task.displayLocationName ? `${task.displayLocationCode || ''} ${task.displayLocationName}`.trim() : '',
        task.priority,
        task.status,
        formatDate(task.scheduledDate, {}, language),
        task.assignedTo?.name || t('tasks.page.unassigned')
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `tasks_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Priority group label map
  const PRIORITY_GROUP_LABELS = {
    critical: t('tasks.page.groupCritical'),
    urgent: t('tasks.page.groupUrgent'),
    high: t('tasks.page.groupHigh'),
    medium: t('tasks.page.groupMedium'),
    low: t('tasks.page.groupLow')
  };

  const PRIORITY_GROUP_COLORS = {
    critical: 'text-danger',
    urgent: 'text-warning',
    high: 'text-info',
    medium: 'text-success',
    low: 'text-primary'
  };

  const STATUS_GROUP_LABELS = {
    pending: t('tasks.page.groupPending'),
    assigned: t('tasks.page.groupAssigned'),
    'in-progress': t('tasks.page.groupInProgress'),
    completed: t('tasks.page.groupCompleted')
  };

  // Apply search filter (client-side, no API re-fetch)
  const displayedTasks = searchQuery
    ? tasks.filter(t => {
        const query = searchQuery.toLowerCase();
        return (
          t.title?.toLowerCase().includes(query) ||
          t.displayLocationName?.toLowerCase().includes(query) ||
          t.guestName?.toLowerCase().includes(query) ||
          t.assignedTo?.name?.toLowerCase().includes(query) ||
          t.ticketId?.toLowerCase().includes(query) ||
          t.booking?.confirmationCode?.toLowerCase().includes(query)
        );
      })
    : tasks;

  // Group tasks by selected criteria
  const getGroupedTasks = () => {
    const groups = {};

    if (groupBy === 'priority') {
      groups['critical'] = displayedTasks.filter(t => t.priority === 'critical');
      groups['urgent'] = displayedTasks.filter(t => t.priority === 'urgent');
      groups['high'] = displayedTasks.filter(t => t.priority === 'high');
      groups['medium'] = displayedTasks.filter(t => t.priority === 'medium');
      groups['low'] = displayedTasks.filter(t => t.priority === 'low');
    } else if (groupBy === 'property') {
      displayedTasks.forEach(task => {
        const propertyName = task.displayLocationName || t('tasks.page.propertyUnknown');
        if (!groups[propertyName]) groups[propertyName] = [];
        groups[propertyName].push(task);
      });
    } else if (groupBy === 'status') {
      groups['pending'] = displayedTasks.filter(t => t.status === 'pending');
      groups['assigned'] = displayedTasks.filter(t => t.status === 'assigned');
      groups['in-progress'] = displayedTasks.filter(t => t.status === 'in-progress');
      groups['completed'] = displayedTasks.filter(t => t.status === 'completed');
    } else if (groupBy === 'date') {
      displayedTasks.forEach(task => {
        const date = formatDate(task.scheduledDate, {}, language);
        if (!groups[date]) groups[date] = [];
        groups[date].push(task);
      });
    }

    return groups;
  };

  const dir = isArabic ? 'rtl' : 'ltr';

  if (loading) {
    return (
      <DesktopLayout
        activeNavItem={activeNavItem}
        setActiveNavItem={setActiveNavItem}
        headerTitle={t('tasks.page.headerTitle')}
      >
        <Container className="text-center py-5" dir={dir}>
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">{t('tasks.page.loading')}</p>
        </Container>
      </DesktopLayout>
    );
  }

  /**
   * The working list — everything this page has always shown. Held in a variable so the
   * supervisor tab can sit beside it without the list being restructured, and so a reader who is
   * not a task manager sees exactly what they saw before: no tab bar at all.
   */
  const worklistView = (
    <>
          {/* Statistics Cards */}
          <TaskStatistics statistics={statistics} />

          {/* Filters Bar */}
          <Card className="mb-4">
            <Card.Body>
              {/* Quick Assignee Toggle — mobile-first chip row */}
              <Row className="mb-3">
                <Col xs={12}>
                  <div className="d-flex flex-wrap gap-2 align-items-center">
                    <Button
                      size="sm"
                      variant={filters.assigneeFilter === 'all' ? 'primary' : 'outline-secondary'}
                      className="touch-btn rounded-pill px-3"
                      onClick={() => setFilters({...filters, assigneeFilter: 'all'})}
                    >
                      {t('tasks.page.assigneeAll')}
                    </Button>
                    <Button
                      size="sm"
                      variant={filters.assigneeFilter === 'mine' ? 'primary' : 'outline-primary'}
                      className="touch-btn rounded-pill px-3 d-flex align-items-center gap-1"
                      onClick={() => setFilters({...filters, assigneeFilter: 'mine'})}
                      disabled={!user?.userId}
                    >
                      <FaUser size={12} />
                      <span>{t('tasks.page.assigneeMine')}</span>
                      {filters.assigneeFilter === 'mine' && (
                        <Badge bg="light" text="primary" className="ms-1">
                          {statistics.total}
                        </Badge>
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant={filters.assigneeFilter === 'unassigned' ? 'warning' : 'outline-warning'}
                      className="touch-btn rounded-pill px-3"
                      onClick={() => setFilters({...filters, assigneeFilter: 'unassigned'})}
                    >
                      {t('tasks.page.assigneeUnassigned')}
                    </Button>
                  </div>
                </Col>
              </Row>

              <Row className="g-3 align-items-end">
                {/* Search */}
                <Col xs={12} lg={3}>
                  <InputGroup size="sm">
                    <InputGroup.Text>
                      <FaSearch />
                    </InputGroup.Text>
                    <Form.Control
                      className="touch-input"
                      placeholder={t('tasks.page.searchPlaceholder')}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </InputGroup>
                </Col>

                {/* Date Filter */}
                <Col xs={6} lg={2}>
                  <Form.Select
                    size="sm"
                    className="touch-select"
                    value={filters.dateFilter}
                    onChange={(e) => setFilters({...filters, dateFilter: e.target.value})}
                  >
                    <option value="all">{t('tasks.page.dateAll')}</option>
                    <option value="today">{t('tasks.page.dateToday')}</option>
                    <option value="week">{t('tasks.page.dateWeek')}</option>
                    <option value="month">{t('tasks.page.dateMonth')}</option>
                  </Form.Select>
                </Col>

                {/* Status Filter */}
                <Col xs={6} lg={2}>
                  <Form.Select
                    size="sm"
                    className="touch-select"
                    value={filters.statusFilter}
                    onChange={(e) => setFilters({...filters, statusFilter: e.target.value})}
                  >
                    <option value="active">{t('tasks.page.statusActive')}</option>
                    <option value="all">{t('tasks.page.statusAll')}</option>
                    <option value="pending">{t('tasks.page.statusPending')}</option>
                    <option value="assigned">{t('tasks.page.statusAssigned')}</option>
                    <option value="in-progress">{t('tasks.page.statusInProgress')}</option>
                    <option value="completed">{t('tasks.page.statusCompleted')}</option>
                  </Form.Select>
                </Col>

                {/* Priority Filter */}
                <Col xs={6} lg={2}>
                  <Form.Select
                    size="sm"
                    className="touch-select"
                    value={filters.priorityFilter}
                    onChange={(e) => setFilters({...filters, priorityFilter: e.target.value})}
                  >
                    <option value="all">{t('tasks.page.priorityAll')}</option>
                    <option value="critical">{t('tasks.priority.critical')}</option>
                    <option value="urgent">{t('tasks.priority.urgent')}</option>
                    <option value="high">{t('tasks.priority.high')}</option>
                    <option value="medium">{t('tasks.priority.medium')}</option>
                    <option value="low">{t('tasks.priority.low')}</option>
                  </Form.Select>
                </Col>

                {/* Property Filter */}
                <Col xs={6} lg={2}>
                  <Form.Select
                    size="sm"
                    className="touch-select"
                    value={filters.propertyFilter}
                    onChange={(e) => setFilters({...filters, propertyFilter: e.target.value})}
                  >
                    <option value="all">{t('tasks.page.propertyAll')}</option>
                    {properties.map(prop => (
                      <option key={prop._id} value={prop._id}>
                        {prop.name}
                      </option>
                    ))}
                  </Form.Select>
                </Col>

                {/* Booking Filter */}
                <Col xs={12} sm={6} lg={2}>
                  <Form.Select
                    size="sm"
                    className="touch-select"
                    value={filters.bookingFilter}
                    onChange={(e) => setFilters({...filters, bookingFilter: e.target.value})}
                  >
                    <option value="all">{t('tasks.page.bookingAll')}</option>
                    <option value="with-booking">{t('tasks.page.bookingWith')}</option>
                    <option value="without-booking">{t('tasks.page.bookingWithout')}</option>
                  </Form.Select>
                </Col>

                {/* Task Type Filter */}
                <Col xs={12} sm={6} lg={2}>
                  <Form.Select
                    size="sm"
                    className="touch-select"
                    value={filters.typeFilter}
                    onChange={(e) => setFilters({...filters, typeFilter: e.target.value})}
                  >
                    <option value="all">{t('tasks.page.typeAll')}</option>
                    {TASK_TYPE_FILTERS.map(type => (
                      <option key={type} value={type}>{t(`tasks.type.${type}`)}</option>
                    ))}
                  </Form.Select>
                </Col>
              </Row>

              {/* View Controls */}
              <Row className="mt-3">
                <Col className="d-flex justify-content-between align-items-center">
                  <div className="d-flex gap-2">
                    <ButtonGroup size="sm">
                      <Button
                        className="touch-btn"
                        variant={viewMode === 'cards' ? 'primary' : 'outline-primary'}
                        onClick={() => setViewMode('cards')}
                      >
                        <FaTh /> {t('tasks.page.viewCards')}
                      </Button>
                      <Button
                        className="touch-btn"
                        variant={viewMode === 'list' ? 'primary' : 'outline-primary'}
                        onClick={() => setViewMode('list')}
                      >
                        <FaList /> {t('tasks.page.viewList')}
                      </Button>
                    </ButtonGroup>

                    {viewMode === 'cards' && (
                      <Form.Select
                        size="sm"
                        className="touch-select"
                        value={groupBy}
                        onChange={(e) => setGroupBy(e.target.value)}
                        style={{ width: 'auto' }}
                      >
                        <option value="priority">{t('tasks.page.groupByPriority')}</option>
                        <option value="property">{t('tasks.page.groupByProperty')}</option>
                        <option value="status">{t('tasks.page.groupByStatus')}</option>
                        <option value="date">{t('tasks.page.groupByDate')}</option>
                      </Form.Select>
                    )}
                  </div>

                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Error Alert */}
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {bulkSkipped > 0 && (
            <Alert variant="warning" dismissible onClose={() => setBulkSkipped(0)}>
              {t('tasks.guestAssessment.bulkSkipped')} {formatCount(bulkSkipped, language)}
            </Alert>
          )}

          {/* Tasks Display */}
          {displayedTasks.length === 0 ? (
            <Card>
              <Card.Body className="text-center py-5">
                <FaTasks size={48} className="text-muted mb-3" />
                <h5>
                  {filters.assigneeFilter === 'mine'
                    ? t('tasks.page.emptyMineTitle')
                    : t('tasks.page.emptyTitle')}
                </h5>
                <p className="text-muted">
                  {filters.assigneeFilter === 'mine'
                    ? t('tasks.page.emptyMineText')
                    : t('tasks.page.emptyText')}
                </p>
                {filters.assigneeFilter !== 'mine' && (
                  <Button
                    variant="primary"
                    className="touch-btn"
                    onClick={() => setShowCreateModal(true)}
                  >
                    <FaPlus className="ms-1" /> {t('tasks.page.emptyCreateBtn')}
                  </Button>
                )}
              </Card.Body>
            </Card>
          ) : viewMode === 'list' ? (
            <TaskListTable
              tasks={displayedTasks}
              onStatusUpdate={handleStatusUpdate}
              onAssign={handleAssign}
              onComplete={handleComplete}
              onBulkComplete={handleBulkComplete}
              onEdit={handleEdit}
              selectedTasks={selectedTasks}
              setSelectedTasks={setSelectedTasks}
            />
          ) : (
            // Cards View - Grouped
            Object.entries(getGroupedTasks()).map(([group, groupTasks]) => (
              groupTasks.length > 0 && (
                <div key={group} className="mb-4">
                  <h5 className="mb-3">
                    {groupBy === 'priority' && (
                      <><span className={PRIORITY_GROUP_COLORS[group]}>{PRIORITY_GROUP_LABELS[group]}</span> ({groupTasks.length})</>
                    )}
                    {groupBy === 'property' && <>{group} ({groupTasks.length})</>}
                    {groupBy === 'status' && (
                      <>{STATUS_GROUP_LABELS[group] || group} ({groupTasks.length})</>
                    )}
                    {groupBy === 'date' && <>{group} ({groupTasks.length})</>}
                  </h5>
                  <Row>
                    {groupTasks.map(task => (
                      <Col key={task._id} lg={6} xl={4} className="mb-3">
                        <TaskCard
                          task={task}
                          onStatusUpdate={handleStatusUpdate}
                          onAssign={handleAssign}
                          onEdit={handleEdit}
                        />
                      </Col>
                    ))}
                  </Row>
                </div>
              )
            ))
          )}
    </>
  );

  return (
    <DesktopLayout
      activeNavItem={activeNavItem}
      setActiveNavItem={setActiveNavItem}
      headerTitle={t('tasks.page.headerTitle')}
    >
      <Container fluid className="py-4" dir={dir}>
        {/* Quick Actions */}
        <Row className="mb-3">
          <Col>
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2">
              <p className="text-muted mb-0">
                {t('tasks.page.totalTasks')} {statistics.total} {t('tasks.page.taskUnit')}
                {statistics.critical > 0 && <span className="text-danger fw-bold"> • {statistics.critical} {t('tasks.page.critical')}</span>}
                {statistics.overdue > 0 && <span className="text-warning fw-bold"> • {statistics.overdue} {t('tasks.page.overdue')}</span>}
              </p>
              <div className="d-flex gap-2">
                <Button
                  variant="success"
                  className="touch-btn"
                  onClick={() => setShowCreateModal(true)}
                >
                  <FaPlus className="ms-1" /> {t('tasks.page.newTask')}
                </Button>
                <Button
                  variant="outline-primary"
                  className="touch-btn"
                  onClick={handleRefresh}
                  disabled={refreshing}
                >
                  {refreshing ? <Spinner animation="border" size="sm" /> : <FaSync />}
                </Button>
              </div>
            </div>
          </Col>
        </Row>

      {canSupervise ? (
        <Tabs
          activeKey={activeTab}
          onSelect={(key) => setActiveTab(key)}
          className="mb-3 task-tabs"
        >
          <Tab eventKey="worklist" title={t('tasks.attention.tabWorklist')}>
            {activeTab === 'worklist' && worklistView}
          </Tab>
          <Tab eventKey="attention" title={t('tasks.attention.tabAttention')}>
            {activeTab === 'attention' && (
              <AttentionTab
                refreshSignal={refreshSignal}
                onOpenPerformance={() => setActiveTab('performance')}
                openTaskId={deepLinkTaskId}
                onDeepLinkHandled={clearDeepLinkTask}
              />
            )}
          </Tab>
          <Tab eventKey="performance" title={t('tasks.performance.tab')}>
            {activeTab === 'performance' && <PerformanceTab refreshSignal={refreshSignal} />}
          </Tab>
        </Tabs>
      ) : worklistView}

        <GuestAssessmentModal
          show={!!assessingTask}
          task={assessingTask}
          onHide={() => { setAssessingTask(null); loadTasks(false); }}
          onConfirm={handleGuestQuestionConfirm}
        />

        {/* Create Task Modal */}
        {showCreateModal && (
          <CreateTaskModal
            show={showCreateModal}
            onHide={() => setShowCreateModal(false)}
            onTaskCreated={() => {
              setShowCreateModal(false);
              loadTasks(false);
            }}
          />
        )}
      </Container>
    </DesktopLayout>
  );
};

export default Tasks;
